import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { LoggerService } from '../../../infra/logger/logger.service';
import { HttpService } from '../../http/http.service';
import { FindingsAggregatedByServiceSeverity, FindingModel } from 'src/app/models/api/finding.model';
import { LabelBindingModel } from 'src/app/models/api/common';
import { AutocompleteSearchResultsV2Model } from 'src/app/models/api/autocompleteSearchResultsV2.model';
import { FindingThreadDetailsModel, FindingThreadModel } from 'src/app/models/api/finding-thread.model';
import {
  AiFindingThreadResponseModel,
  FindingEvidenceSensitiveContentResponseModel,
  FindingThreadDetailsResponseModel,
  ThreadActivitiesResponseModel,
  toFindingThreadDetailsModel,
  toFindingThreadModel,
} from './ai-finding-thread-response.model';

import { FindingsFilterValuesResponse } from 'src/app/models/api/findings-filter-values.model';

@Injectable({
  providedIn: 'root',
})
export class AiFindingsHttpService {
  public readonly defaultPageSize: number = 50;

  constructor(
    private readonly _httpService: HttpService,
    private readonly _logger: LoggerService
  ) {}

  /**
   * @public Get single inventory resource by id
   * @returns an Observable{InventoryResourceModel} with the resource returned from server
   */
  public getAiFinding(integrationId: string, findingId: string, sourcePipeline?: string): Observable<FindingModel> {
    const url = `detection/integrations/${integrationId}/findings/${findingId}`;
    let httpParams: HttpParams | undefined;
    if (sourcePipeline) {
      httpParams = new HttpParams().append('sourcePipeline', sourcePipeline);
    }
    return this._httpService.get<FindingModel>(url, undefined, httpParams, true);
  }

  public getEvidenceSensitiveContent(
    integrationId: string,
    findingId: string,
    evidenceId: string,
    sourcePipeline?: string
  ): Observable<FindingEvidenceSensitiveContentResponseModel> {
    const baseUrl = `detection/integrations/${integrationId}/findings/${findingId}/evidences/${evidenceId}`;
    const newUrl = `${baseUrl}/getSensitiveContent:execute`;
    const oldUrl = `${baseUrl}/sensitive-content`;

    const body = sourcePipeline ? { sourcePipeline: [sourcePipeline] } : {};

    // Try POST on new path first (new backend), fallback to GET on old path (old backend)
    return this._httpService
      .post<object, FindingEvidenceSensitiveContentResponseModel>(newUrl, body, undefined, undefined, true)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404 || error.status === 405) {
            // Old backend: 404 if new path doesn't exist, 405 if method not allowed
            return this._httpService.get<FindingEvidenceSensitiveContentResponseModel>(
              oldUrl,
              undefined,
              undefined,
              true
            );
          }
          throw error;
        })
      );
  }

  /**
   * @public Start getting first page of inventory resources
   * @returns an Observable{InventoryResourceModel} with the resource returned from server
   */
  public startGetFindings(
    filterOptions: ISelectedFilters,
    sortData?: SortColumn,
    pageSize = this.defaultPageSize
  ): Observable<JsonHalCollection<FindingModel>> {
    const url = 'detection/findings';
    const httpParams = this._createFindingsQueryParams(filterOptions, pageSize, sortData);
    return this._httpService.get<JsonHalCollection<FindingModel>>(url, undefined, httpParams, true);
  }

  public getFindingsByThreadId(threadId: string, sourcePipeline?: string): Observable<JsonHalCollection<FindingModel>> {
    const url = 'detection/findings';
    let httpParams = new HttpParams().append('threadId', threadId);
    if (sourcePipeline) {
      httpParams = httpParams.append('sourcePipeline', sourcePipeline);
    }
    return this._httpService.get<JsonHalCollection<FindingModel>>(url, undefined, httpParams, true);
  }
  /**
   * @public Continue getting inventory resources using a continuation token
   * @returns an Observable{InventoryResourceModel} with the resource returned from server
   */
  public getFindingsByContinuationToken(
    continuationToken: string,
    sourcePipeline?: string
  ): Observable<JsonHalCollection<FindingModel>> {
    const url = 'detection/findings-pages';
    let httpParams = new HttpParams().append('continuationToken', continuationToken);
    if (sourcePipeline) {
      httpParams = httpParams.append('sourcePipeline', sourcePipeline);
    }
    return this._httpService.get<JsonHalCollection<FindingModel>>(url, undefined, httpParams, true);
  }

  public startGetThreads(
    filterOptions: ISelectedFilters,
    sortData?: SortColumn,
    pageSize = this.defaultPageSize
  ): Observable<JsonHalCollection<FindingThreadModel>> {
    const url = 'detection/thread-findings';
    const httpParams = this._createFindingsQueryParams(filterOptions, pageSize, sortData);
    return this._httpService
      .get<JsonHalCollection<AiFindingThreadResponseModel>>(url, undefined, httpParams, true)
      .pipe(
        map((response: JsonHalCollection<AiFindingThreadResponseModel>) => {
          return {
            ...response,
            _embedded: {
              items: response._embedded.items.map((item) => toFindingThreadModel(item)),
            },
          };
        })
      );
  }

  public getThreadsByContinuationToken(
    continuationToken: string,
    sourcePipeline?: string
  ): Observable<JsonHalCollection<FindingThreadModel>> {
    const url = 'detection/thread-findings-pages';
    let httpParams = new HttpParams().append('continuationToken', continuationToken);
    if (sourcePipeline) {
      httpParams = httpParams.append('sourcePipeline', sourcePipeline);
    }
    return this._httpService
      .get<JsonHalCollection<AiFindingThreadResponseModel>>(url, undefined, httpParams, true)
      .pipe(
        map((response: JsonHalCollection<AiFindingThreadResponseModel>) => {
          return {
            ...response,
            _embedded: {
              items: response._embedded.items.map((item) => toFindingThreadModel(item)),
            },
          };
        })
      );
  }

  public getThread(threadId: string, sourcePipeline?: string): Observable<FindingThreadDetailsModel> {
    const url = `detection/thread-findings/${threadId}`;
    let httpParams: HttpParams | undefined;
    if (sourcePipeline) {
      httpParams = new HttpParams().append('sourcePipeline', sourcePipeline);
    }
    return this._httpService
      .get<FindingThreadDetailsResponseModel>(url, undefined, httpParams, true)
      .pipe(map((response) => toFindingThreadDetailsModel(response)));
  }

  public getThreadActivitiesSensitiveContent(
    integrationId: string,
    threadId: string
  ): Observable<ThreadActivitiesResponseModel> {
    const url = `detection/integrations/${integrationId}/threads/getThreadActivities:execute`;
    const body = { ids: [threadId] };
    return this._httpService.post<typeof body, ThreadActivitiesResponseModel>(url, body, undefined, undefined, true);
  }

  /**
   * @public Get single inventory autocomplete options
   * @returns an Observable{AutocompleteSearchResultsModel} with the displayNames
   */
  public getAutocompleteOptions(query: string, filterName: string): Observable<AutocompleteSearchResultsV2Model[]> {
    const supportedFilterNames = ['environment', 'actor'];
    const mappedFilterName: Record<string, string> = {
      environment: 'environment',
      actor: 'mainActorDisplayName',
    };

    if (!supportedFilterNames.includes(filterName)) {
      return of([]);
    }

    const url = `detection/autocomplete/findings`;
    const mapped = mappedFilterName[filterName];
    const httpParams = this._createFindingsQueryParams(
      { query, filter_name: mapped },
      this.defaultPageSize,
      undefined,
      false
    );

    return this._httpService.get<AutocompleteSearchResultsV2Model[]>(url, undefined, httpParams, true);
  }

  public removeFileFromRag(integrationId: string, fileUrl: string): Observable<string> {
    const url = `detection/integrations/${integrationId}/findings/actions/exclude-file-from-rag/${fileUrl}`;
    return this._httpService.post<string, string>(url, '', undefined, undefined, true);
  }

  /**
   * @public Bind label to finding
   * @returns an Observable{LabelBindingModel} with the created label binding
   */
  public bindLabelToFinding(
    findingId: string,
    integrationId: string,
    labelId: string,
    description?: string
  ): Observable<LabelBindingModel> {
    const url = 'label-bindings';
    const findingType = 'finding';
    const requestBody = {
      labelId: labelId,
      entityType: findingType,
      entityId: findingId,
      description: description,
    };
    return this._httpService.post<typeof requestBody, LabelBindingModel>(url, requestBody, undefined, undefined, false);
  }

  /**
   * @public Unbind label from finding
   * @returns an Observable{void} indicating successful deletion
   */
  public unbindLabelFromFinding(
    findingId: string,
    integrationId: string,
    labelBinding: LabelBindingModel
  ): Observable<void> {
    const url = `label-bindings/${labelBinding.id}`;
    return this._httpService.delete<void>(url, undefined, undefined, false);
  }

  /**
   * @public Get findings aggregated by service severity for dashboard
   * @param filterOptions Optional filter options including integration filtering
   * @returns an Observable{FindingsAggregatedByServiceSeverityResponse[]} with the aggregated findings data
   */
  public getFindingsAggregatedByServiceSeverity(
    filterOptions?: ISelectedFilters
  ): Observable<FindingsAggregatedByServiceSeverity[]> {
    const url = 'detection/findings-aggregated-by-service-severity';
    const httpParams = this._createFindingsAggregatedQueryParams(filterOptions);
    return this._httpService.get<FindingsAggregatedByServiceSeverity[]>(url, undefined, httpParams, true);
  }

  public getFilterValues(): Observable<FindingsFilterValuesResponse> {
    const url = 'detection/findings-filter-values';
    return this._httpService.get<FindingsFilterValuesResponse>(url, undefined, undefined, true);
  }

  private _createFindingsAggregatedQueryParams(filterOptions?: ISelectedFilters): HttpParams {
    let httpParams = new HttpParams();

    if (!filterOptions) {
      return httpParams;
    }

    if (filterOptions.integration && filterOptions.integration.length > 0) {
      filterOptions.integration.forEach((integrationId) => {
        httpParams = httpParams.append('integrationId', integrationId);
      });
    }
    return httpParams;
  }

  private _createFindingsQueryParams(
    filterOptions: ISelectedFilters,
    pageSize: number,
    sortData?: SortColumn,
    shouldLimitResultsAmount = true
  ): HttpParams {
    let httpParams = new HttpParams();
    if (shouldLimitResultsAmount) {
      httpParams = httpParams.append('pageSize', pageSize);
    }

    httpParams = this._appendFindingsFilterQueryParams(httpParams, filterOptions);

    if (sortData) {
      httpParams = this._appendFindingsSortQueryParams(httpParams, sortData);
    }
    return httpParams;
  }

  private _appendFindingsFilterQueryParams(httpParams: HttpParams, filterOptions: ISelectedFilters): HttpParams {
    if (filterOptions.resourceId && filterOptions.resourceId.length > 0) {
      httpParams = httpParams.append('id', filterOptions.resourceId.join(','));
    }

    if (filterOptions.integration && filterOptions.integration.length > 0) {
      httpParams = httpParams.append('integrationId', filterOptions.integration.join(','));
    }

    if (filterOptions.severity && filterOptions.severity.length > 0) {
      httpParams = httpParams.append('severity', filterOptions.severity.join(','));
    }

    if (filterOptions.threadId && filterOptions.threadId.length > 0) {
      httpParams = httpParams.append('threadId', filterOptions.threadId.join(','));
    }

    const owaspValues: string[] = [];
    if (filterOptions.owaspLlm && filterOptions.owaspLlm.length > 0) {
      owaspValues.push(...filterOptions.owaspLlm);
    }
    if (filterOptions.owaspAsi && filterOptions.owaspAsi.length > 0) {
      owaspValues.push(...filterOptions.owaspAsi);
    }
    if (owaspValues.length > 0) {
      httpParams = httpParams.append('owasp', owaspValues.join(','));
    }

    if (filterOptions.genAiMatrix && filterOptions.genAiMatrix.length > 0) {
      httpParams = httpParams.append('genAiAttack', filterOptions.genAiMatrix.join(','));
    }

    if (filterOptions.aiService && filterOptions.aiService.length > 0) {
      httpParams = httpParams.append('aiService', filterOptions.aiService.join(','));
    }

    if (filterOptions.fromDate) {
      httpParams = httpParams.append('sinceTimestamp', filterOptions.fromDate.toISOString());
    }

    if (filterOptions.toDate) {
      httpParams = httpParams.append('untilTimestamp', filterOptions.toDate.toISOString());
    }

    if (filterOptions.search) {
      httpParams = httpParams.append('textSearch', filterOptions.search);
    }

    if (filterOptions.filter_name && filterOptions.filter_name.length > 0) {
      httpParams = httpParams.append('filterName', filterOptions.filter_name);
    }

    if (filterOptions.filter_id && filterOptions.filter_id.length > 0) {
      httpParams = httpParams.append('filterId', `${filterOptions.filter_id}`);
    }

    if (filterOptions.query) {
      httpParams = httpParams.append('query', filterOptions.query);
    }

    if (filterOptions.environment && filterOptions.environment.length > 0) {
      httpParams = httpParams.append('environmentId', filterOptions.environment.join(','));
    }

    if (filterOptions.actor && filterOptions.actor.length > 0) {
      httpParams = httpParams.append('mainActorId', filterOptions.actor.join(','));
    }

    if (filterOptions.label && filterOptions.label.length > 0) {
      httpParams = httpParams.append('labelIds', filterOptions.label.join(','));
    }

    if (filterOptions.tag && filterOptions.tag.length > 0) {
      filterOptions.tag.forEach((tag) => {
        httpParams = httpParams.append('tags', tag);
      });
    }

    if (filterOptions.dataItem && filterOptions.dataItem.length > 0) {
      httpParams = httpParams.append('dataItemId', filterOptions.dataItem.join(','));
    }

    if (filterOptions.source_pipeline && filterOptions.source_pipeline.length > 0) {
      httpParams = httpParams.append('sourcePipeline', filterOptions.source_pipeline.join(','));
    }

    return httpParams;
  }

  private _appendFindingsSortQueryParams(httpParams: HttpParams, sortData: SortColumn): HttpParams {
    const sortFieldString: string | undefined = sortData.column.sortKey;
    const ascOrDescString = sortData.isDesc ? 'desc' : 'asc';

    if (sortFieldString) {
      httpParams = httpParams.append('sortBy', `[${sortFieldString}(${ascOrDescString})]`);
    } else {
      this._logger.error(`Sort inventory column ${sortData.column.propertyKey} was not implemented`);
    }
    return httpParams;
  }
}
